import type { HttpClient } from "@dl/web-core-js/api";
import type { ProductType, WarehouseDTO } from "./product";

/**
 * One product's transfer picture for a single destination warehouse.
 *
 * A row per product, not per movement, and every product the destination stocks
 * appears — including the ones needing nothing. A planner filling a shipment has
 * to see the near misses as well as the shortfalls, and a list that silently
 * omits every zero cannot be checked against reality.
 *
 * Recomputed on every request and never stored. These cannot be read off the
 * production pipeline either: a lot only exists where something must be
 * produced, and the case a transfer answers is exactly the one where nothing
 * does.
 */
export interface TransferSuggestionDTO {
  product_id: number;
  sku?: string | null;
  product_name?: string | null;

  /** On hand less reserved and less committed, at the destination only. */
  destination_available: number;
  /**
   * Physically present at the destination, before any commitment. Optional
   * because a backend older than these fields simply will not send them.
   */
  destination_on_hand?: number;
  /** Committed to orders already picked. */
  destination_reserved?: number;
  /** Ordered but not yet committed. */
  destination_in_order?: number;
  /** Weekly units the destination is responsible for, in the markets it serves. */
  weekly_sales: number;
  /** Weeks it lasts on what it holds. Carries the no-demand sentinel. */
  weeks_remaining: number;

  /** Everything upstream holds, whether or not it can spare it. */
  source_total: number;
  /** What upstream can give up without going short itself over the same horizon. */
  source_safe: number;
  /**
   * Units to move: the shortfall rounded to the nearest five, then capped by
   * what upstream can safely give. The cap can leave a non-multiple, which is
   * deliberate — shipping the three that exist beats asking for five that do not.
   */
  suggested_units: number;
  /** Ranks are per type: a bundle's rank is among bundles. */
  product_type?: ProductType | null;
  /** How it sells in each market the destination ships it to (usually one). */
  markets?: TransferMarketSalesDTO[];
}

/** Velocity and rank in one market, as on the products page. */
export interface TransferMarketSalesDTO {
  market: "CA" | "US";
  velocity: number;
  /** Null when not ranked (only AVAILABLE and PENDING_LAUNCH products are). */
  sales_rank?: number | null;
}

/** Stable row key: one row per product for the chosen destination. */
export function transferRowId(row: TransferSuggestionDTO): string {
  return String(row.product_id);
}

export class TransferPlanClient {
  constructor(private readonly http: HttpClient) {}

  /** Warehouses a shipment can actually be sent to, meaning they have a lane in. */
  destinations(): Promise<WarehouseDTO[]> {
    return this.http.get<WarehouseDTO[]>("/warehouse/transfer/destination");
  }

  /**
   * What to ship into one warehouse to leave it holding `weeks` of stock.
   * Ranked by suggested quantity, so the list reads as a loading order.
   */
  /**
   * @param weeks        cover the destination should end up holding
   * @param reserveWeeks how long each source keeps enough for itself
   */
  getPlan(
    warehouseId: number,
    weeks: number,
    reserveWeeks: number,
  ): Promise<TransferSuggestionDTO[]> {
    return this.http.get<TransferSuggestionDTO[]>("/warehouse/transfer/plan", {
      query: { warehouse_id: warehouseId, weeks, reserve_weeks: reserveWeeks },
    });
  }
}
